<template>
  <div class="d-flex flex-column" :style="{ gap: '10px' }">
    <v-toolbar flat>
      <v-toolbar-action>
        <div class="d-flex" :style="{ gap: '10px' }">
          <v-btn color="green" @click="(e) => (createDialog = { open: true })">
            <v-icon>mdi-plus</v-icon>
            Create Consumer
          </v-btn>
          <v-btn text @click="$router.push('/consumer-ticket-requests')">
            <v-icon>mdi-ticket</v-icon>
            Consumer Ticket Requests
          </v-btn>
          <export-consumer-loan />
        </div>
      </v-toolbar-action>
      <v-spacer />
      <v-btn
        v-if="authPermissions.has('sales:view')"
        text
        @click="$router.push('/inventories/sale')"
      >
        <v-icon>mdi-cash-register</v-icon>
        Cash Sales
      </v-btn>
    </v-toolbar>
    <!-- Filters & Actions -->
    <div
      class="d-flex flex-column flex-sm-row mb-3 align-center flex-wrap"
      :style="{ gap: '10px' }"
    >
      <v-switch
        :input-value="tableFilters.noEmpty"
        label="With Loans Only"
        hide-details
        :true-value="true"
        :false-value="false"
        class="mt-0"
        @change="(v) => (tableFilters = { ...tableFilters, noEmpty: !!v })"
      />
      <v-text-field
        v-model="tableFilters.search"
        outlined
        label="Search by name, loan ID, or product"
        dense
        clearable
        hide-details
        style="min-width: 220px"
        @keyup.enter="(v) => (tableFilters = { ...tableFilters })"
        @change="(v) => (tableFilters = { ...tableFilters })"
      />
      <v-select
        :value="tableFilters.status"
        outlined
        :items="statusOptions"
        label="Loan Status"
        hint="Filter by the highest-priority loan status per consumer"
        persistent-hint
        dense
        clearable
        multiple
        chips
        small-chips
        deletable-chips
        style="min-width: 220px"
        @change="(v) => (tableFilters = { ...tableFilters, status: v })"
      />

      <v-btn color="primary" @click="fetchConsumers">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>

    <!-- Table -->
    <v-data-table
      :loading="table.loading"
      :headers="table.headers"
      :items="table.items"
      :options.sync="table.options"
      :server-items-length="table.total"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
    >
      <template #item.balance="{ value }">
        <td class="text-end">
          {{ formatAccounting(value || 0) }}
        </td>
      </template>
      <template #item.remarks="{ value }">
        <v-chip
          :color="
            {
              paid: 'green',
              'not due': 'blue',
              'almost due': 'orange',
              'due today': 'amber',
              overdue: 'red',
            }[value] || 'grey'
          "
          dark
          small
        >
          {{ value || 'no loans' }}
        </v-chip>
      </template>
      <template #item.contactNo1="{ value }">
        <td class="text-center">
          {{ value }}
        </td>
      </template>
      <template #item.contactNo2="{ value }">
        <td class="text-center">
          {{ value }}
        </td>
      </template>
      <template #item._config="{ item }">
        <div class="flex-row d-flex" :style="{ gap: '4px' }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="authPermissions.has('consumers:edit')"
                class="mx-2"
                fab
                dark
                small
                v-bind="attrs"
                color="yellow darken-2"
                v-on="on"
                @click="editConsumer(item)"
              >
                <v-icon dark>
                  mdi-pencil
                </v-icon>
              </v-btn>
            </template>
            <span>Edit Consumer</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template
              v-if="authPermissions.has('consumer_loans:view')"
              v-slot:activator="{ on, attrs }"
            >
              <v-btn
                class="mx-2"
                fab
                dark
                small
                v-bind="attrs"
                color="teal"
                v-on="on"
                @click="
                  $router.push({
                    name: 'Consumers.Sales',
                    params: { consumer: item.id },
                  })
                "
              >
                <v-icon dark>
                  mdi-cash-multiple
                </v-icon>
              </v-btn>
            </template>
            <span>Sales</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template
              v-if="authPermissions.has('consumer_loans:view')"
              v-slot:activator="{ on, attrs }"
            >
              <v-btn
                class="mx-2"
                fab
                dark
                small
                v-bind="attrs"
                color="purple"
                v-on="on"
                @click="
                  $router.push({
                    name: 'Consumers.Transactions',
                    params: { consumer: item.id },
                  })
                "
              >
                <v-icon dark>
                  mdi-swap-horizontal
                </v-icon>
              </v-btn>
            </template>
            <span>Transactions</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template
              v-if="authPermissions.has('consumer_loans:view')"
              v-slot:activator="{ on, attrs }"
            >
              <v-btn
                class="mx-2"
                fab
                dark
                small
                v-bind="attrs"
                color="indigo"
                :loading="statementLoadingId === item.id"
                v-on="on"
                @click="printStatement(item)"
              >
                <v-icon dark>
                  mdi-file-document-outline
                </v-icon>
              </v-btn>
            </template>
            <span>Statement of Account</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template
              v-if="authPermissions.has('consumer_loans:view')"
              v-slot:activator="{ on, attrs }"
            >
              <v-btn
                v-if="item.hasAccount"
                class="mx-2"
                fab
                dark
                small
                v-bind="attrs"
                color="yellow darken-2"
                v-on="on"
                @click="openEditConsumerUser(item.id)"
              >
                <v-icon dark>
                  mdi-account-edit
                </v-icon>
              </v-btn>
              <v-btn
                v-else
                class="mx-2"
                fab
                dark
                small
                v-bind="attrs"
                color="blue"
                v-on="on"
                @click="openCreateConsumerUser(item.id)"
              >
                <v-icon dark>
                  mdi-account
                </v-icon>
              </v-btn>
            </template>
            <span v-if="item.hasAccount">Edit Account</span>
            <span v-else>Add Account</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <v-dialog v-model="editDialog.open" width="400px" persistent eager>
      <v-card width="400px">
        <v-toolbar color="warning">
          <v-toolbar-title class="black--text">
            Update Consumer
          </v-toolbar-title>
        </v-toolbar>
        <edit-consumer
          ref="editConsumerRef"
          @close="closeEditConsumer"
          @success="fetchConsumers"
        />
      </v-card>
    </v-dialog>
    <v-dialog v-model="createDialog.open" width="400px" persistent eager>
      <v-card width="400px">
        <v-toolbar color="success">
          <v-toolbar-title>New Consumer</v-toolbar-title>
        </v-toolbar>
        <create-consumer
          ref="createConsumerRef"
          @close="closeCreateConsumer"
          @success="fetchConsumers"
        />
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="consumerUserEditDialog.open"
      width="400px"
      persistent
      eager
    >
      <v-card width="400px">
        <v-toolbar color="warning">
          <v-toolbar-title class="black--text">
            Edit Account
          </v-toolbar-title>
        </v-toolbar>
        <edit-consumer-user
          ref="editConsumerUserRef"
          @close="closeEditConsumerUser"
          @success="fetchConsumers"
        />
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="consumerUserCreateDialog.open"
      width="400px"
      persistent
      eager
    >
      <v-card width="400px">
        <v-toolbar color="success">
          <v-toolbar-title>Create Account</v-toolbar-title>
        </v-toolbar>
        <create-consumer-user
          ref="createConsumerUserRef"
          @close="closeCreateConsumerUser"
          @success="fetchConsumers"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { authAxios } from '../../../../../extra-functions/axios.extra'
import { printStatementOfAccount } from '../../../../../pdf/statement-of-account.print'
import {
  ErrorPopup,
  LoadingPopup,
  formatAccounting,
  useWatchEffect,
} from '../../../../../lib/utils'

const [watchEffect, clearEffect] = useWatchEffect()

export default {
  name: 'ConsumerIndexV2',
  components: {
    EditConsumer: () => import('./Edit.vue'),
    CreateConsumer: () => import('./Create.vue'),
    CreateConsumerUser: () => import('./CreateUser.vue'),
    EditConsumerUser: () => import('./EditUser.vue'),
    ExportConsumerLoan: () => import('./ExportLoan.vue'),
  },
  data() {
    return {
      editDialog: {
        open: false,
        item: null,
      },
      createDialog: {
        open: false,
      },
      consumerUserCreateDialog: {
        open: false,
      },
      consumerUserEditDialog: {
        open: false,
      },
      statementLoadingId: null,
      table: {
        headers: [
          { text: 'Name', value: 'name' },
          { text: 'Address', value: 'address' },
          { text: 'Contact #1', value: 'contactNo1', sortable: false },
          { text: 'Contact #2', value: 'contactNo2', sortable: false },
          { text: 'Balance', value: 'balance' },
          { text: 'Remarks', value: 'remarks' },
          { text: 'Config', value: '_config', sortable: false },
        ],
        loading: false,
        total: 0,
        items: [],
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: [],
        },
      },
      tableFilters: {
        noEmpty: true,
        search: null,
        status: ['paid', 'not due', 'almost due', 'due today', 'overdue'],
      },
      statusOptions: ['paid', 'not due', 'almost due', 'due today', 'overdue'],
    }
  },
  computed: {
    ...mapState({
      authPermissions: (state) => state.auth.userPermissions,
      storeId: (state) => state.settings.storeId,
    }),
  },
  watch: {
    tableFilters() {
      if (this.table.options.page !== 1) {
        this.table.options.page = 1 // options watcher triggers the fetch
        return
      }
      this.fetchConsumers()
    },
    'table.options': {
      handler() {
        this.fetchConsumers()
      },
      deep: true,
    },
  },
  created() {
    this.fetchConsumers()
  },
  destroyed() {
    clearEffect()
  },
  methods: {
    fetchConsumers() {
      watchEffect(async (onCleanup) => {
        try {
          // Mutate table fields instead of replacing the object — replacing
          // this.table re-triggers the deep 'table.options' watcher (Vue 2
          // deep watchers fire on any dependency change, even with an
          // identical value), causing an infinite fetch loop
          this.table.loading = true
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const { noEmpty, ...restFilters } = this.tableFilters
          const { page, itemsPerPage, sortBy, sortDesc } = this.table.options
          const response = await authAxios().get('/v2/consumers', {
            params: {
              ...restFilters,
              no_empty: noEmpty ? 1 : 0,
              page,
              per_page: itemsPerPage,
              sort_by: sortBy?.[0] || undefined,
              sort_desc: sortDesc?.[0] ? 1 : 0,
            },
            signal: controller.signal,
          })
          this.table.items = response.data.data.map(item => ({
            ...item,
            hasAccount: !!item.has_account,
            contactNo1: item.contact_no_1,
            contactNo2: item.contact_no_2,
          }))
          this.table.total = response.data.total
        } catch (e) {
          console.error(e)
          LoadingPopup.close()
          ErrorPopup.fire('Error', 'Oops something went wrong')
        } finally {
          this.table.loading = false
        }
      }, 'ConsumerIndexV2_fetchConsumers')
    },
    editConsumer(item) {
      this.editDialog = { open: true, item }
      this.$refs?.editConsumerRef.setConsumer?.(item)
    },
    closeEditConsumer() {
      this.editDialog = { open: false, item: null }
    },
    closeCreateConsumer() {
      this.createDialog = { open: false }
    },
    closeCreateConsumerUser() {
      this.consumerUserCreateDialog = { open: false }
    },
    closeEditConsumerUser() {
      this.consumerUserEditDialog = { open: false }
    },
    openCreateConsumerUser(consumerId) {
      this.$refs.createConsumerUserRef?.setConsumerId?.(consumerId)
      this.consumerUserCreateDialog = { open: true }
    },
    async printStatement(item) {
      if (this.statementLoadingId) return
      this.statementLoadingId = item.id
      try {
        await printStatementOfAccount({
          consumerId: item.id,
          name: item.name,
          address: item.address,
          storeId: this.storeId,
        })
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Error', 'Oops something went wrong')
      } finally {
        this.statementLoadingId = null
      }
    },
    openEditConsumerUser(consumerId) {
      this.$refs.editConsumerUserRef?.setConsumerId?.(consumerId)
      this.$refs.editConsumerUserRef?.fetchConsumerUser?.()
      this.consumerUserEditDialog = { open: true }
    },
    formatAccounting,
  },
}
</script>
